import React from 'react';
import { ChevronRight } from 'lucide-react';
import { FunnelStage } from '../data/mockData';
interface ConversionFunnelProps {
  funnel: FunnelStage[];
}
export function ConversionFunnel({
  funnel
}: ConversionFunnelProps) {
  const getStatusColors = (status: 'green' | 'yellow' | 'red') => {
    switch (status) {
      case 'green':
        return 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400';
      case 'yellow':
        return 'bg-amber-500/10 border-amber-500/30 text-amber-400';
      case 'red':
        return 'bg-red-500/10 border-red-500/30 text-red-400';
    }
  };
  const getStatusIndicator = (status: 'green' | 'yellow' | 'red') => {
    switch (status) {
      case 'green':
        return 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]';
      case 'yellow':
        return 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]';
      case 'red':
        return 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]';
    }
  };
  const getProgressBarColor = (status: 'green' | 'yellow' | 'red') => {
    switch (status) {
      case 'green':
        return 'bg-emerald-500';
      case 'yellow':
        return 'bg-amber-500';
      case 'red':
        return 'bg-red-500';
    }
  };
  const getProgressTrackColor = (status: 'green' | 'yellow' | 'red') => {
    switch (status) {
      case 'green':
        return 'bg-emerald-500/20';
      case 'yellow':
        return 'bg-amber-500/20';
      case 'red':
        return 'bg-red-500/20';
    }
  };
  const getDeltaText = (stage: FunnelStage) => {
    const delta = stage.percentage - stage.benchmark;
    if (delta > 0) return `+${delta}pp`;
    if (delta < 0) return `${delta}pp`;
    return 'On target';
  };
  return <section className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-6 h-full flex flex-col" data-id="element-7">
      <div className="flex justify-between items-center mb-6" data-id="element-8">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2" data-id="element-9">
          <span className="w-1.5 h-5 bg-blue-500 rounded-full" data-id="element-10"></span>
          Conversion Pipeline
        </h2>
        <div className="flex gap-3 text-xs text-slate-400" data-id="element-11">
          <div className="flex items-center gap-1" data-id="element-12">
            <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-13"></div> ≥
            Benchmark
          </div>
          <div className="flex items-center gap-1" data-id="element-14">
            <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-15"></div> Near
          </div>
          <div className="flex items-center gap-1" data-id="element-16">
            <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-17"></div> Below
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0" data-id="element-18">
        {funnel.map((stage, index) => <div key={stage.name} className="flex items-center w-full sm:w-auto flex-1" data-id="element-19">
            <div className={`relative flex-1 flex flex-col items-center justify-center p-4 rounded-lg border ${getStatusColors(stage.status)} transition-all duration-300 hover:bg-opacity-20`} data-id="element-20">
              <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${getStatusIndicator(stage.status)}`} data-id="element-21"></div>
              <span className="text-slate-300 text-xs sm:text-sm font-medium mb-1 text-center" data-id="element-22">
                {stage.name}
              </span>
              <span className="text-2xl sm:text-3xl font-bold" data-id="element-23">
                {stage.percentage}%
              </span>

              {/* Benchmark context */}
              <div className="w-full mt-3 pt-2 border-t border-slate-700/30" data-id="element-24">
                <div className="flex items-center justify-between mb-1" data-id="element-25">
                  <span className="text-xs text-slate-400 font-medium" data-id="element-26">
                    Target: {stage.benchmark}%
                  </span>
                  <span className={`text-[11px] font-bold ${stage.status === 'green' ? 'text-emerald-400' : stage.status === 'yellow' ? 'text-amber-400' : 'text-red-400'}`} data-id="element-27">
                    {getDeltaText(stage)}
                  </span>
                </div>
                <div className={`h-1.5 w-full rounded-full ${getProgressTrackColor(stage.status)}`} data-id="element-28">
                  <div className={`h-full rounded-full ${getProgressBarColor(stage.status)} transition-all duration-500`} style={{
                width: `${Math.min(stage.percentage / stage.benchmark * 100, 100)}%`
              }} data-id="element-29" />
                </div>
              </div>
            </div>

            {index < funnel.length - 1 && <div className="hidden sm:flex items-center justify-center w-8 text-slate-600" data-id="element-30">
                <ChevronRight className="w-6 h-6" data-id="element-31" />
              </div>}
            {index < funnel.length - 1 && <div className="sm:hidden flex items-center justify-center h-6 text-slate-600 my-1" data-id="element-32">
                <ChevronRight className="w-5 h-5 rotate-90" data-id="element-33" />
              </div>}
          </div>)}
      </div>
    </section>;
}